// Arrival lifecycle hub — structured knowledge from docs/knowledge/lifecycle-arrival.yaml
// L1 = official, L2 = practical, L3 = traveler tips (editorial)
import { EXPLORE_IMAGES } from './exploreImages';
import { OFFICIAL_SOURCES, LAST_VERIFIED, VISA_FEES_USD } from './officialSources';

export const ARRIVAL_HUB = {
	title: 'Arriving in Nepal — Kathmandu Airport Guide',
	eyebrow: 'Plan your trip',
	hero: '/images/hero-nepal.jpg',
	lastVerified: LAST_VERIFIED,
	primarySource: OFFICIAL_SOURCES.immigrationTia,
	secondarySources: [OFFICIAL_SOURCES.immigration, OFFICIAL_SOURCES.customs, OFFICIAL_SOURCES.ntc],
	quickFacts: [
		{ value: '30–90 min', label: 'Immigration (peak season)' },
		{ value: `USD ${VISA_FEES_USD[30]}`, label: '30-day visa (if VOA)' },
		{ value: 'NPR ~900', label: 'Prepaid taxi to Thamel' },
		{ value: '24/7', label: 'Prepaid taxi counter' },
	],
	lifecycleFlow: [],
	sections: [
		{
			id: 'landing',
			step: 1,
			question: 'What happens when my plane lands?',
			source: OFFICIAL_SOURCES.immigrationTia,
			answer: 'Tribhuvan International Airport (TIA) is Nepal\'s only international airport. After disembarking, follow signs to immigration. All foreign nationals except Indian citizens need a valid visa unless exempt. Machine-readable passports (MRP) are required — handwritten passports are not accepted per TIA Immigration arrival information.',
			tips: ['Fill the online visa form before landing if you need visa on arrival — saves kiosk time.', 'October–November and March–April are peak seasons — allow 60–90 minutes for immigration.'],
		},
		{
			id: 'arrival-card',
			step: 2,
			question: 'Do I need an arrival card?',
			source: OFFICIAL_SOURCES.immigrationTia,
			answer: 'Yes. Foreign nationals must present a properly completed arrival card at the immigration desk along with passport and visa documents. Cards are distributed on the flight or available at the airport.',
			tips: ['Write your first-night hotel address — or "Thamel, Kathmandu" if undecided.', 'Keep a photo of your hotel booking on your phone.'],
		},
		{
			id: 'visa-form',
			step: 3,
			question: 'Should I complete the visa form before landing?',
			source: OFFICIAL_SOURCES.immigrationOnline,
			answer: 'Strongly recommended. Complete the Tourist Visa form at nepaliport.immigration.gov.np before arrival. You receive a submission receipt with a barcode — print it (valid 15 days). Alternatively, use kiosk machines at the airport. If the barcode expires, fill the form again.',
			tips: ['Screenshot the barcode on your phone as backup.', 'Children need their own passport and visa — not listed on a parent\'s passport.'],
		},
		{
			id: 'visa-payment',
			step: 4,
			question: 'Where do I pay the visa fee?',
			source: OFFICIAL_SOURCES.immigration,
			answer: `Pay at the bank/payment counter BEFORE the immigration desk. Official fees (multiple entry): 15 days USD ${VISA_FEES_USD[15]}; 30 days USD ${VISA_FEES_USD[30]}; 90 days USD ${VISA_FEES_USD[90]}. TIA on-arrival visa counters are numbered 5–12 (TIA Immigration arrival information).`,
			table: [
				{ duration: '15 days', fee: `USD ${VISA_FEES_USD[15]}` },
				{ duration: '30 days', fee: `USD ${VISA_FEES_USD[30]}` },
				{ duration: '90 days', fee: `USD ${VISA_FEES_USD[90]}` },
			],
			tips: ['Carry clean USD notes ($50s and $100s). Card acceptance varies — cash is the reliable backup.', 'Choose 90 days if you might trek longer — extensions cost more per day than buying upfront.'],
		},
		{
			id: 'immigration',
			step: 5,
			question: 'What happens at immigration?',
			source: OFFICIAL_SOURCES.immigrationTia,
			answer: 'Present passport (6+ months validity), completed arrival card, visa payment receipt, and online form barcode receipt. The officer stamps your visa. Processing typically takes 30–60 minutes; longer during peak season. Nationals of Afghanistan, Cameroon, Ethiopia, Ghana, Iraq, Liberia, Nigeria, Palestine, Somalia, Eswatini, Syria, and Zimbabwe cannot obtain visa on arrival — confirm the current list on immigration.gov.np before travel.',
			tips: ['Answer simply: "Tourism, trekking/culture, X weeks."', 'Indian nationals: no visa required; carry passport or voter ID proving Indian nationality.'],
		},
		{
			id: 'baggage-customs',
			step: 6,
			question: 'Baggage claim and customs',
			source: OFFICIAL_SOURCES.customs,
			answer: 'Collect baggage from the carousel, then pass customs. Declare dutiable or restricted goods at the red channel. Drones, professional filming equipment, and antiques may require permits or declaration — see CAAN and Department of Archaeology rules. Green channel if nothing to declare.',
			tips: ['Mark luggage distinctly — many similar trekking backpacks on Everest flights.', 'Undeclared drones risk confiscation — check CAAN rules before packing.'],
		},
		{
			id: 'atm-exchange',
			step: 7,
			question: 'ATM and currency exchange at the airport',
			source: null,
			editorial: true,
			answer: 'Licensed currency exchange counters operate in the international arrivals area. ATMs from Nepali banks dispense NPR — typical daily limits NPR 10,000–35,000 with foreign transaction fees from your home bank. Airport exchange rates are worse than city rates — exchange only what you need for taxi and SIM.',
			tips: ['Withdraw NPR 10,000–15,000 for your first day if not using a prepaid taxi voucher.', 'Notify your home bank before travel to avoid card blocks.', 'It is illegal to import or export Nepali rupee notes — exchange after arrival.'],
		},
		{
			id: 'wifi',
			step: 8,
			question: 'Is there WiFi at the airport?',
			source: OFFICIAL_SOURCES.ntc,
			answer: 'Nepal Telecom (NT) WiFi hotspot is available at Kathmandu Airport International terminal per NT\'s official WiFi location list. Subscribe by texting WIFI10 (1 hour) or WIFI30 (5 hours) to 1416. Useful for activating eSIM or ride-apps if you have an NT SIM.',
			tips: ['Do not rely on airport WiFi for visa form completion — do that before landing.', 'Free terminal WiFi may require mobile registration — unreliable for urgent tasks.'],
		},
		{
			id: 'sim',
			step: 9,
			question: 'Can I buy a SIM card at the airport?',
			source: OFFICIAL_SOURCES.ntc,
			answer: 'NTC and Ncell both operate prepaid tourist SIM services in Nepal. Registration requires passport copy/photo per telecom regulations. NTC prepaid tariffs and data packages are published at ntc.net.np; Ncell packages at ncell.com.np. Airport counters may operate in arrivals — compare NTC vs Ncell packages before choosing.',
			tips: ['Buy SIM before leaving terminal if you need Pathao/inDrive immediately (requires local number for OTP).', 'Thamel shops can set up SIM next morning if airport counter is closed — slightly cheaper.', 'NT eSIM available at esim.ntc.net.np for compatible phones — activate over WiFi.'],
		},
		{
			id: 'taxi',
			step: 10,
			question: 'How do I get from the airport to my hotel?',
			source: null,
			editorial: true,
			answer: 'Use the official prepaid taxi counter inside or just outside international arrivals — open 24/7. Tell the counter your destination, pay a fixed fare in NPR (or USD at some counters), receive a voucher, and give it to the driver. Green number plates indicate government-regulated prepaid taxis. Pathao and inDrive ride-apps are cheaper but require a local SIM for OTP.',
			table: [
				{ destination: 'Thamel', fee: 'NPR ~700–900 (day)' },
				{ destination: 'Thamel (after 21:00)', fee: 'NPR ~800–1,000 + surcharge' },
				{ destination: 'Lazimpat / Paknajol', fee: 'NPR ~1,000' },
				{ destination: 'Patan (Lalitpur)', fee: 'NPR ~1,000–1,200' },
				{ destination: 'Bhaktapur', fee: 'NPR ~1,500–1,800' },
			],
			tips: ['Ignore touts at the exit — use only the prepaid counter or a pre-booked hotel pickup.', 'Hotel airport pickup is the least stressful option for first-time arrivals.', 'Traffic to Thamel worst 4–7pm weekdays — allow 45–60 minutes.'],
		},
		{
			id: 'scams',
			step: 11,
			question: 'What scams should I watch for on arrival?',
			source: null,
			editorial: true,
			answer: 'Common arrival scams: unlicensed taxi drivers quoting inflated fares; "your hotel is closed" — call your hotel yourself; fake tour operators in the arrivals hall; unofficial currency exchangers with poor rates or counterfeit notes. Use prepaid taxi counter, licensed exchange, and book tours through verified platforms.',
			tips: ['Tourist Police: 1144 (toll-free). Police: 100. Ambulance: 102.', 'CIWEC Clinic Travel Medicine Center is the standard expat/tourist hospital in Kathmandu.'],
		},
		{
			id: 'day-one',
			step: 12,
			question: 'What should I do on Day 1 in Kathmandu?',
			source: null,
			editorial: true,
			answer: 'Check into your hotel and rest if jet-lagged — Kathmandu is at ~1,400m and mild altitude affects some travelers. Activate SIM, withdraw NPR, download Pathao/inDrive. Short walk in Thamel or visit Boudhanath if energy permits. Do not start a high-altitude trek the same day you fly in. If trekking, buy permits and check gear in Thamel the next morning.',
			tips: ['October arrivals: Dashain festival may close some shops — check dates and book ahead.', 'Eat at busy restaurants — high turnover means fresher food.', 'Bottled water only — avoid tap water.'],
		},
	],
	germanyOctober: {
		title: 'Arriving from Germany in October?',
		points: [
			'German nationals are eligible for visa on arrival at TIA (Embassy of Nepal, Berlin). No need to visit the Berlin embassy unless you prefer a pre-issued visa.',
			'October is peak trekking season — immigration queues can be long. Complete nepaliport.immigration.gov.np before landing.',
			'Dashain (major festival) often falls in October — some businesses close during main festival days. Weather is excellent for trekking and sightseeing.',
			'Nepali Embassy Berlin: Guerickestrasse 27, 10587 Berlin — +49 (030) 343599-20 — de.nepalembassy.gov.np',
			'Prepaid taxi to Thamel ~NPR 900. Buy NTC or Ncell SIM in arrivals. First day: rest, explore Thamel or Boudhanath, do not trek same day.',
		],
	},
	faqs: [
		{ q: 'Can I pay the visa fee by credit card at TIA?', a: 'Nepal Tourism Board notes international credit cards may be accepted at some entry points. The immigration process involves a bank counter — carry USD cash as reliable backup. Verify current payment methods on immigration.gov.np before travel.' },
		{ q: 'How long does airport arrival take?', a: 'Official estimate: 30–60 minutes for visa on arrival at immigration, plus baggage and customs. Peak season (Oct–Nov, Mar–Apr) can take 90+ minutes total.' },
		{ q: 'Is there an ATM before immigration?', a: 'Visa payment is before immigration at the bank counter (USD). NPR ATMs and exchange counters are in the arrivals hall after immigration — withdraw NPR for taxi and SIM.' },
		{ q: 'Can I use Pathao from the airport?', a: 'Yes, but you need a local SIM for OTP verification. Prepaid taxi counter is simpler for first-time arrivals without a SIM.' },
		{ q: 'Do I need a hotel booking to enter?', a: 'Not always checked, but immigration may ask your address. Have your first-night hotel name ready on the arrival card and visa form.' },
		{ q: 'What emergency numbers do I need?', a: 'Tourist Police: 1144 (toll-free, English). Police: 100. Ambulance: 102. Fire: 101. See the Safety & Emergency hub for hospitals and altitude advice.' },
	],
	officialLinks: [
		{ label: 'TIA Arrival Information', url: 'https://tia.immigration.gov.np/en/page/arrival-information' },
		{ label: 'TIA Immigration FAQ', url: OFFICIAL_SOURCES.immigrationTia.url },
		{ label: 'Online Visa Form', url: OFFICIAL_SOURCES.immigrationOnline.url },
		{ label: 'Tourist Visa (Immigration Dept)', url: OFFICIAL_SOURCES.immigration.url },
		{ label: 'Department of Customs', url: OFFICIAL_SOURCES.customs.url },
		{ label: 'NT WiFi at Airport', url: OFFICIAL_SOURCES.ntc.url },
		{ label: 'NTC Prepaid Tariffs', url: 'https://www.ntc.net.np/services/gsm-prepaid-service' },
		{ label: 'Ncell Packages', url: OFFICIAL_SOURCES.ncell.url },
	],
	related: [
		{ label: 'Visa & immigration', to: '/visa' },
		{ label: 'Getting a SIM card', to: '/guides/getting-a-sim-card-in-nepal' },
		{ label: 'Currency & ATMs', to: '/guides/currency-atms-money-in-nepal' },
		{ label: 'Transportation', to: '/transportation' },
		{ label: 'October travel guide', to: '/seasonal-discovery' },
		{ label: 'Kathmandu Valley', to: '/destinations' },
	],
};
